import { useSyncExternalStore } from 'react'

/**
 * Coordinates an "only one open at a time" state across unrelated
 * components without requiring a shared provider. Each caller passes a
 * stable `key` (e.g. a row id) and gets back `{ isOpen, open, close }`.
 * Calling `open()` on any key automatically closes whichever key was
 * previously open.
 */
let currentKey: string | null = null
const listeners = new Set<() => void>()

function setCurrent(next: string | null) {
  if (currentKey === next) return
  currentKey = next
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

interface SingleOpenState {
  isOpen: boolean
  open: () => void
  close: () => void
}

export function useSingleOpen(key: string): SingleOpenState {
  const isOpen = useSyncExternalStore(
    subscribe,
    () => currentKey === key,
    () => false,
  )
  return {
    isOpen,
    open: () => setCurrent(key),
    close: () => {
      if (currentKey === key) setCurrent(null)
    },
  }
}
